import { RouteName } from '@/base/constants/route'
import userRoutes from './user'
import searchAdsIntelRoutes from './search-ads-intel'
import pdfReportRoutes from './pdf-report'
import onboardingRoutes from './onboarding'
import marketIntelRoutes from './market-intel'
import callbackRoutes from './callback'
import authRoutes from './auth'
import asoIntelRoutes from './aso-intel'
import appIntelRoutes from './app-intel'
import adIntelRoutes from './ad-intel'

/**
 * MA User Product Model
 *
 * @typedef Product
 * @property {string} productName - Product name.
 */

/**
 * MA User Feature Model
 *
 * @typedef Feature
 * @property {string} featureName - Feature name.
 */

/**
 * MA Route Authorization Object Model
 *
 * @typedef Authorize
 * @property {string<'EVERY' | 'SOME'>} checkType - Authorization control is done according to the control type.
 * @property {(Array<Product>|Array<Feature>|Array<Product & Feature>)} features - Defines which page the user has access to or not.
 */

/**
 * MA Route Meta Object Model
 *
 * @typedef RouteMeta
 * @property {string} title - Display name.
 * @property {boolean} parent - If true, it is considered as a parent. Defined to create the parent section in the side menu.
 * @property {string} icon - Icon name for the side menu.
 * @property {string<'title' | 'headline' | 'sub-headline'>} titleFont - if this feature is defined, no router-link is created in the side menu for the corresponding route. This feature is used for grouping.
 * @property {Array} requiredParams - Necessary parameters to continue using the page.
 * @property {Array} requiredQueries - Necessary parameters to continue using the page.
 * @property {Array} ignoredQueries - Parameters that should be excluded from the route during navigation.
 *                                    These are typically used to avoid retaining unnecessary query parameters when navigating between pages.
 *                                    For example, when switching between tabs that share some query parameters but differ in others,
 *                                    `ignoredQueries` ensures that only relevant parameters remain in the route.
 * @property {boolean} ignoreFromMenu - ignore route model from side menu.
 * @property {boolean} ignoreFromMenuDependUnauthorized - ignore route model from side menu depend authorized.
 * @property {Function} ignoreFromMenuDependCustomFn - ignore route model from side menu depend custom function.
 * @property {boolean} ignoreParentFromMenu - ignore route model parent from side menu. (Ex: User Route Parent)
 * @property {boolean} ignoreChildrenFromMenu - ignore route model children from side menu.
 * @property {boolean} disableAuthCheck - Ignore user status. Typically used for auth routes.
 * @property {boolean} hidePageTitleOnHeader - Hide page title on header.
 * @property {boolean} navigationWithoutAppTrack - App search redirects to App Intel page.
 * @property {string} redirectRouteName - App search redirect to a specific page, if not defined, the default redirect will work. e.g. `redirectRouteName: RouteName.APP_INTELLIGENCE_APP_ANALYSIS`
 * @property {boolean} disabledAutoScroll - If the value is set to true and the page `to.name` and `from.name` are equal, the page scroll position is preserved.
 * @property {string} analyticsPageName - Represents the page name from the analytics dashboard.
 * @property {string} productFamily - Represents the product family in the analytics dashboard.
 * @property {number} lastDisabledDayCount - Indicates how many days ago from today the dates are valid.
 * @property {boolean} hideGlobalSearch - Hide global search bar.
 */

/**
 * MA Route Object Model
 *
 * @typedef Route
 * @property {string} path - Percentage encoded pathname section of the URL.
 * @property {string} name - Name of the matched record.
 * @property {object} redirect - Where to redirect if the route is directly matched. The redirection happens before any navigation guard and triggers a new navigation with the new target location.
 * @property {RouteMeta} meta - Merged meta properties from all the matched route records.
 * @property {(Function | object | Array | boolean)} props - Allow passing down params as props to the component rendered by router-view.
 * @property {(Function | Array)} beforeEnter - Route guards.
 */

/**
 * MA Dashboard route schema.
 * The side menu is automatically created according to this route diagram.
 *
 * For example:
 * For this url structure the following nested structure is created in the side menu.
 *
 * /app-intelligence/compass/explore/:trackId?
 *
 * Side menu
 * +---------------------+
 * | App Intelligence    |
 * | +-----------------+ |
 * | | Compass         | |
 * | | +------------+  | |
 * | | | Explore    |  | |
 * | | +------------+  | |
 * | +-----------------+ |
 * +---------------------+
 */
const routes = [
  authRoutes,
  onboardingRoutes,
  {
    path: '/:pathMatch(.*)*',
    name: RouteName.PATH_MATCH,
    redirect: { name: RouteName.NOT_FOUND },
    meta: {
      title: 'Path Match',
    },
    component: () => import('@/pages/main/layout/OnlyContent.vue'),
  },
  {
    path: '/404',
    name: RouteName.NOT_FOUND,
    meta: {
      title: 'Not Found',
      analyticsPageName: '404',
    },
    component: () => import('@/layouts/NotFound.vue'),
  },
  {
    path: '/impersonation',
    name: RouteName.AUTH_IMPERSONATION_LAYOUT,
    meta: {
      title: 'Impersonation Layout',
    },
    component: () => import('@/layouts/Blank.vue'),
    children: [
      {
        path: '',
        name: RouteName.AUTH_IMPERSONATION,
        meta: {
          title: 'Impersonation',
        },
        component: () => import('@/pages/auth/ImpersonationAuth.vue'),
      },
    ],
  },
  {
    path: '/',
    name: RouteName.MAIN,
    meta: {
      title: 'Main',
    },
    redirect: { name: RouteName.APP_INTELLIGENCE },
    component: () => import('@/layouts/Blank.vue'),
    children: [
      userRoutes,
      asoIntelRoutes,
      appIntelRoutes,
      searchAdsIntelRoutes,
      adIntelRoutes,
      marketIntelRoutes,
      pdfReportRoutes,
      callbackRoutes,
    ],
  },
]

export default routes
